import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that ensures user is authenticated before accessing protected pages
 * Checks both MSAL authentication and sessionStorage token
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  // Check for session token (primary check - fastest)
  const hasToken = !!sessionStorage.getItem("entra_token");

  // Show loading only if MSAL is initializing AND we don't have a token
  // If we have a token, proceed immediately (don't wait for MSAL)
  if (inProgress !== InteractionStatus.None && !hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#32196c] mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-gray-600 font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Check authentication: user must have either MSAL auth OR valid session token
  const isUserAuthenticated = isAuthenticated || hasToken;

  // If not authenticated, redirect to auth page
  if (!isUserAuthenticated) {
    // Store the attempted location so we can redirect back after login
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;

